// src/edu-world/core/world-engine.ts
// World_Engine: Phaser 3 scene for EduWorld vertical slice (Phase 1)
// Now uses real PNG assets with scale adjustments.

import Phaser from 'phaser';
import type { ZoneConfig, CameraConfig, PlayerConfig } from '../types';
import { PlayerController } from './player-controller';
import { BarrierEditor } from './barrier-editor';
import { PlacementEditor } from './placement-editor';
import { MicroInteractionAnimator } from '../systems/micro-interaction-animations';
import { MobileDpad } from '../ui/mobile-dpad';
import { ZoneTransitionSystem } from '../systems/zone-transition';
import type { ZoneGridEntry } from '../systems/zone-transition';
import type { EventBus } from './event-bus';
import barrierPolygons from '../content/zones/barriers.json';

export interface WorldEngineConfig {
  zone: ZoneConfig;
  player: PlayerConfig;
  camera: CameraConfig;
  eventBus: EventBus;
}

export class WorldEngine extends Phaser.Scene {
  private config!: WorldEngineConfig;
  private playerController!: PlayerController;
  private eventBus!: EventBus;
  private miAnimator!: MicroInteractionAnimator;
  private mobileDpad!: MobileDpad;
  private zoneTransition!: ZoneTransitionSystem;

  constructor() {
    super({ key: 'WorldEngine' });
  }

  init(data: WorldEngineConfig): void {
    if (data && data.zone) {
      this.config = data;
    } else {
      const regData = this.game.registry.get('edu-world-config');
      this.config = {
        zone: regData.zone,
        player: regData.player,
        camera: regData.camera,
        eventBus: regData.eventBus,
      };
    }
    this.eventBus = this.config.eventBus;
  }

  preload(): void {
    // ── Loading progress → update loading bar in HTML ──
    this.load.on('progress', (value: number) => {
      const bar = document.getElementById('loading-bar');
      const percent = document.getElementById('loading-percent');
      if (bar) bar.style.width = `${Math.round(value * 100)}%`;
      if (percent) percent.textContent = `${Math.round(value * 100)}%`;
    });
    this.load.on('complete', () => {
      const playBtn = document.getElementById('play-btn');
      const percent = document.getElementById('loading-percent');
      if (playBtn) playBtn.style.display = 'inline-block';
      if (percent) percent.textContent = 'Pronto!';
    });

    // Load single unified map background (original quality, no upscale)
    this.load.image('mapa-completo', new URL('../assets/zones/mapa-completo.png', import.meta.url).href);

    // Load character and NPC assets (original 3)
    this.load.image('player', new URL('../assets/player.png', import.meta.url).href);
    this.load.spritesheet('player-walk', new URL('../assets/player-walk.png', import.meta.url).href, {
      frameWidth: 313,
      frameHeight: 313,
    });
    this.load.image('rosa', new URL('../assets/Rosa.png', import.meta.url).href);
    this.load.image('tome', new URL('../assets/tome.png', import.meta.url).href);
    this.load.image('professor', new URL('../assets/professor.png', import.meta.url).href);
    this.load.image('flores-murchas', new URL('../assets/flores-murchas.png', import.meta.url).href);
    this.load.image('flores-bonitas', new URL('../assets/flores-bonitas.png', import.meta.url).href);
    this.load.image('sapo', new URL('../assets/sapo.png', import.meta.url).href);
    this.load.image('borboleta', new URL('../assets/borboleta.png', import.meta.url).href);
    this.load.image('joaninha', new URL('../assets/joaninha.png', import.meta.url).href);

    // Load new NPCs (32 characters)
    const npcNames = [
      'avo-joaquim', 'galinha-pintinha', 'burro-martim',
      'capitao-ondas', 'caranguejo-pincas', 'gaivota-maria',
      'papagaio-pirata', 'macaco-coco', 'tartaruga-velha',
      'palhaco-risota', 'magico-estrela', 'urso-peluche',
      'dona-amelia', 'carteiro-carlos', 'gelado-frio',
      'cao-fiel', 'bibliotecaria-luna',
      'piloto-nuvem', 'mala-viajante', 'controlador-radar',
      'tratador-simao', 'flamingo-rosa', 'elefante-trombeta',
      'bombeiro-bravo', 'cao-dalmata', 'chefe-sirene',
      'princesa-aurora', 'dragao-faisca', 'cavaleiro-valente',
      'astronauta-luna', 'robo-beep', 'alien-zorp',
    ];
    for (const name of npcNames) {
      this.load.image(name, new URL(`../assets/npcs/${name}.png`, import.meta.url).href);
    }

    // Load micro-interaction assets (24 objects)
    const microNames = [
      'mi-tractor', 'mi-espantalho', 'mi-castelo-areia', 'mi-peixe-saltar',
      'mi-canhao', 'mi-coco', 'mi-carrossel', 'mi-balao',
      'mi-fonte', 'mi-relogio', 'mi-baloico', 'mi-pegadas',
      'mi-helice', 'mi-manga-vento', 'mi-animal-zoo', 'mi-pato',
      'mi-sirene', 'mi-mangueira', 'mi-ponte', 'mi-poco',
      'mi-chamas', 'mi-antena', 'mi-borboleta', 'mi-sapo',
    ];
    for (const name of microNames) {
      this.load.image(name, new URL(`../assets/micro/${name}.png`, import.meta.url).href);
    }
  }

  create(): void {
    const { zone, player, camera } = this.config;

    // ── Single unified map background (original quality) ──
    // Fallback: if image fails to load, show warm friendly placeholder (Task 27.2)
    if (this.textures.exists('mapa-completo')) {
      const bg = this.add.image(zone.width / 2, zone.height / 2, 'mapa-completo');
      bg.setDisplaySize(zone.width, zone.height);
      bg.setDepth(0);
    } else {
      // Placeholder fallback — soft pastel zones with friendly icons
      // Each zone gets a unique warm colour so the child still perceives variety
      this.renderPlaceholderMap(zone);
    }

    // ── Zone Transition System (Task 27.1) ──
    // Detects which zone the player is in and emits events on transitions.
    // With a unified background image, transitions are inherently seamless (no loading).
    const zoneData = zone as any;
    const zoneGrid: ZoneGridEntry[] = zoneData.zones ?? [];
    const gridCols = zoneData.grid?.cols ?? 4;
    const gridRows = zoneData.grid?.rows ?? 3;
    this.zoneTransition = new ZoneTransitionSystem(
      this.eventBus,
      zoneGrid,
      gridCols,
      gridRows,
      zone.width,
      zone.height
    );

    // ── Flores murchas (garden area near Rosa in Parque zone) ──
    const floresMurchas = this.add.image(519, 189, 'flores-murchas');
    floresMurchas.setScale(0.015);
    floresMurchas.setDepth(2);
    floresMurchas.setData('type', 'wilted-flower');
    floresMurchas.setData('element-id', 'parque-flores');

    // ── World NPCs (distributed across zones — positions from editor) ──
    const worldNpcs = [
      { id: 'avo-joaquim', x: 213, y: 158, scale: 0.0365 },
      { id: 'galinha-pintinha', x: 369, y: 242, scale: 0.0248 },
      { id: 'burro-martim', x: 187, y: 33, scale: 0.02 },
      { id: 'capitao-ondas', x: 899, y: 142, scale: 0.0286 },
      { id: 'caranguejo-pincas', x: 995, y: 156, scale: 0.014 },
      { id: 'gaivota-maria', x: 799, y: 49, scale: 0.0269 },
      { id: 'papagaio-pirata', x: 1286, y: 217, scale: 0.0277 },
      { id: 'macaco-coco', x: 1336, y: 137, scale: 0.0332 },
      { id: 'tartaruga-velha', x: 1224, y: 212, scale: 0.0227 },
      { id: 'palhaco-risota', x: 328, y: 559, scale: 0.0308 },
      { id: 'magico-estrela', x: 221, y: 460, scale: 0.022 },
      { id: 'urso-peluche', x: 120, y: 601, scale: 0.024 },
      { id: 'dona-amelia', x: 494, y: 514, scale: 0.0277 },
      { id: 'carteiro-carlos', x: 528, y: 616, scale: 0.0225 },
      { id: 'gelado-frio', x: 686, y: 426, scale: 0.0273 },
      { id: 'cao-fiel', x: 1032, y: 494, scale: 0.0235 },
      { id: 'bibliotecaria-luna', x: 990, y: 525, scale: 0.0217 },
      { id: 'piloto-nuvem', x: 1284, y: 520, scale: 0.0294 },
      { id: 'mala-viajante', x: 1399, y: 550, scale: 0.0179 },
      { id: 'controlador-radar', x: 1174, y: 442, scale: 0.019 },
      { id: 'tratador-simao', x: 129, y: 803, scale: 0.0344 },
      { id: 'flamingo-rosa', x: 217, y: 692, scale: 0.018 },
      { id: 'elefante-trombeta', x: 327, y: 718, scale: 0.0258 },
      { id: 'bombeiro-bravo', x: 592, y: 805, scale: 0.0276 },
      { id: 'cao-dalmata', x: 704, y: 828, scale: 0.016 },
      { id: 'chefe-sirene', x: 563, y: 853, scale: 0.0271 },
      { id: 'princesa-aurora', x: 950, y: 738, scale: 0.027 },
      { id: 'dragao-faisca', x: 913, y: 816, scale: 0.0474 },
      { id: 'cavaleiro-valente', x: 947, y: 900, scale: 0.0345 },
      { id: 'astronauta-luna', x: 1297, y: 810, scale: 0.0361 },
      { id: 'robo-beep', x: 1472, y: 855, scale: 0.0342 },
      { id: 'alien-zorp', x: 1226, y: 775, scale: 0.0238 },
    ];

    for (const npc of worldNpcs) {
      if (this.textures.exists(npc.id)) {
        const sprite = this.add.image(npc.x, npc.y, npc.id);
        sprite.setScale(npc.scale);
        sprite.setDepth(5);
        // Gentle idle float animation
        this.tweens.add({
          targets: sprite,
          y: npc.y - 2,
          duration: 1500 + Math.random() * 500,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    }

    // ── Micro-interactions (positions from editor) ──
    const microItems = [
      { id: 'mi-tractor', x: 45, y: 105, scale: 0.1298 },
      { id: 'mi-espantalho', x: 360, y: 98, scale: 0.0385 },
      { id: 'mi-castelo-areia', x: 1014, y: 203, scale: 0.0992 },
      { id: 'mi-peixe-saltar', x: 1090, y: 151, scale: 0.0514 },
      { id: 'mi-canhao', x: 1396, y: 204, scale: 0.0771 },
      { id: 'mi-coco', x: 1442, y: 136, scale: 0.0236 },
      { id: 'mi-carrossel', x: 236, y: 503, scale: 0.0928 },
      { id: 'mi-balao', x: 365, y: 518, scale: 0.0277 },
      { id: 'mi-fonte', x: 609, y: 645, scale: 0.049 },
      { id: 'mi-relogio', x: 608, y: 361, scale: 0.1743 },
      { id: 'mi-baloico', x: 830, y: 461, scale: 0.068 },
      { id: 'mi-pegadas', x: 1128, y: 583, scale: 0.0395 },
      { id: 'mi-helice', x: 1422, y: 600, scale: 0.0807 },
      { id: 'mi-manga-vento', x: 1299, y: 617, scale: 0.0569 },
      { id: 'mi-animal-zoo', x: 77, y: 696, scale: 0.0278 },
      { id: 'mi-pato', x: 156, y: 704, scale: 0.0183 },
      { id: 'mi-sirene', x: 447, y: 890, scale: 0.0599 },
      { id: 'mi-mangueira', x: 632, y: 816, scale: 0.0264 },
      { id: 'mi-ponte', x: 876, y: 930, scale: 0.014 },
      { id: 'mi-poco', x: 947, y: 784, scale: 0.0661 },
      { id: 'mi-chamas', x: 1479, y: 739, scale: 0.0741 },
      { id: 'mi-antena', x: 1368, y: 910, scale: 0.0822 },
      { id: 'mi-borboleta', x: 473, y: 190, scale: 0.01 },
      { id: 'mi-sapo', x: 689, y: 199, scale: 0.0228 },
    ];

    // ── Micro-Interaction Animator (proximity-based animations) ──
    this.miAnimator = new MicroInteractionAnimator(this, 80);

    for (const mi of microItems) {
      if (this.textures.exists(mi.id)) {
        const sprite = this.add.image(mi.x, mi.y, mi.id);
        sprite.setScale(mi.scale);
        sprite.setDepth(3);
        sprite.setAlpha(0.85);
        // Register for proximity animation
        this.miAnimator.registerSprite(mi.id, sprite);
      }
    }

    // ── Player ──
    this.playerController = new PlayerController(
      this,
      zone.spawnPoint.x,
      zone.spawnPoint.y,
      player,
      zone,
      this.eventBus
    );

    // ── Camera ──
    this.cameras.main.setBounds(0, 0, zone.width, zone.height);
    this.cameras.main.startFollow(
      this.playerController.getSprite(),
      true,
      camera.lerp,
      camera.lerp
    );
    this.cameras.main.setDeadzone(
      this.cameras.main.width * 0.1,
      this.cameras.main.height * 0.1
    );

    // Zoom — dynamic based on aspect ratio (landscape = less zoom, portrait = more zoom)
    this.updateDynamicZoom();
    this.scale.on('resize', () => this.updateDynamicZoom());

    // Set world bounds
    this.physics.world.setBounds(0, 0, zone.width, zone.height);

    // ── Mobile D-pad (touch devices only) ──
    this.mobileDpad = new MobileDpad(this);

    // ── Barrier Editor (dev mode — press B to toggle) ──
    new BarrierEditor(this, this.playerController);

    // ── Apply hardcoded barriers (from editor export) ──
    this.playerController.setPolygonBarriers(barrierPolygons as { x: number; y: number }[][]);

    // ── Placement Editor (dev mode — press N to toggle, drag sprites) ──
    const placementEditor = new PlacementEditor(this, this.playerController);

    // Store in registry so other scenes (Orchestrator) can register their sprites too
    this.game.registry.set('placementEditor', placementEditor);

    // Register all NPC sprites for placement editing
    for (const npc of worldNpcs) {
      const sprite = this.children.list.find(
        (child) => child instanceof Phaser.GameObjects.Image && child.texture.key === npc.id
      ) as Phaser.GameObjects.Image | undefined;
      if (sprite) placementEditor.register(sprite, npc.id, 'npc');
    }

    // Register all micro-interaction sprites for placement editing
    for (const mi of microItems) {
      const sprite = this.children.list.find(
        (child) => child instanceof Phaser.GameObjects.Image && child.texture.key === mi.id
      ) as Phaser.GameObjects.Image | undefined;
      if (sprite) placementEditor.register(sprite, mi.id, 'micro');
    }

    // Register flores murchas for placement editing
    placementEditor.register(floresMurchas, 'flores-murchas', 'micro');

    // Launch orchestrator now that WorldEngine is fully created
    const regData = this.game.registry.get('edu-world-config');
    if (regData) {
      this.scene.launch('EduWorldOrchestrator', {
        eventBus: this.eventBus,
        content: regData.content,
        playerConfig: regData.player,
      });
    }
  }

  update(_time: number, _delta: number): void {
    // Feed D-pad direction from HTML overlay to player controller
    const dpadDir = (window as any).__dpadDirection;
    if (dpadDir && (dpadDir.x !== 0 || dpadDir.y !== 0)) {
      this.playerController.setExternalDirection(dpadDir);
    } else {
      this.playerController.setExternalDirection({ x: 0, y: 0 });
    }

    this.playerController.update();

    // Update zone transition detection (Task 27.1)
    const pos = this.playerController.getPosition();
    this.zoneTransition.update(pos.x, pos.y);

    // Update MI proximity animations
    this.miAnimator.update(pos.x, pos.y);
  }

  /** Adjust camera zoom based on screen aspect ratio */
  private updateDynamicZoom(): void {
    // With fixed canvas (960x640), zoom controls how much of the map is visible
    // Lower zoom = see more map. The map is 1536x1024.
    // At zoom 1.0, we'd see 960x640 of the map.
    // At zoom 2.0, we see 480x320 of the map.
    const w = this.scale.width;
    const h = this.scale.height;
    const aspect = w / h;

    let zoom: number;
    if (aspect >= 1.4) {
      // Landscape — less zoom, see more map, better image quality
      zoom = 1.8;
    } else {
      // Portrait / square
      zoom = 2.4;
    }

    this.cameras.main.setZoom(zoom);
  }

  getPlayerController(): PlayerController {
    return this.playerController;
  }

  getEventBus(): EventBus {
    return this.eventBus;
  }

  getZoneTransitionSystem(): ZoneTransitionSystem {
    return this.zoneTransition;
  }

  /**
   * Render a friendly placeholder map when the unified background fails to load.
   * Each zone cell gets a distinct soft pastel colour and a friendly emoji icon,
   * so the child still perceives variety without seeing error states.
   * (Requirement 1.8, 1.9)
   */
  private renderPlaceholderMap(zone: ZoneConfig): void {
    const zoneData = zone as any;
    const zones: ZoneGridEntry[] = zoneData.zones ?? [];
    const gridCols: number = zoneData.grid?.cols ?? 4;
    const gridRows: number = zoneData.grid?.rows ?? 3;
    const cellWidth = zone.width / gridCols;
    const cellHeight = zone.height / gridRows;

    // Soft pastel palette — warm, friendly colours (no harsh reds/blacks)
    const pastelColors = [
      0x81c784, // green (quinta)
      0xa5d6a7, // light green (parque)
      0x80deea, // cyan (praia)
      0xffcc80, // orange (ilha pirata)
      0xce93d8, // purple (diversões)
      0xfff59d, // yellow (praça)
      0x90caf9, // blue (escola)
      0xb0bec5, // grey-blue (aeroporto)
      0xc8e6c9, // pale green (zoo)
      0xef9a9a, // salmon (bombeiros)
      0xd7ccc8, // beige (castelo)
      0xb39ddb, // lavender (espaço)
    ];

    // Friendly emoji icons per zone — child-safe, no text
    const zoneIcons = ['🌾', '🌳', '🏖️', '🏴‍☠️', '🎡', '🏘️', '📚', '✈️', '🦁', '🚒', '🏰', '🚀'];

    if (zones.length > 0) {
      // Render each zone as a coloured cell with icon
      for (let i = 0; i < zones.length; i++) {
        const z = zones[i];
        const x = z.gridX * cellWidth + cellWidth / 2;
        const y = z.gridY * cellHeight + cellHeight / 2;
        const color = pastelColors[i % pastelColors.length];

        const rect = this.add.rectangle(x, y, cellWidth - 2, cellHeight - 2, color);
        rect.setDepth(0);
        rect.setAlpha(0.85);

        // Friendly zone icon (subtle)
        const icon = this.add.text(x, y, zoneIcons[i % zoneIcons.length], {
          fontSize: '32px',
        });
        icon.setOrigin(0.5);
        icon.setDepth(1);
        icon.setAlpha(0.4);

        // Mark all zones as placeholder for guidance reduction
        this.zoneTransition.markAsPlaceholder(z.id);
      }
    } else {
      // No zone grid data — single warm green rectangle
      const placeholder = this.add.rectangle(
        zone.width / 2, zone.height / 2,
        zone.width, zone.height, 0x81c784
      );
      placeholder.setDepth(0);

      const hint = this.add.text(zone.width / 2, zone.height / 2, '🌳', {
        fontSize: '48px',
      });
      hint.setOrigin(0.5);
      hint.setDepth(1);
      hint.setAlpha(0.3);
    }
  }
}
